import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { useUnsafeCallback } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import {
  AvatarNameSelectorDocument,
  CostCenterEmployeeListDocument,
  CostCenterEmployeeListQuery,
  CostCentersListDocument,
  EmployeesTeamsDocument,
} from 'generated/resources/graphql'
import uniqBy from 'lodash/uniqBy'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl as buildAvatarUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { EmptyCostCenters } from 'modules/cost_centers/components/Resources/Employees/EmptyCostCenters'
import { CostCenterDetails } from 'modules/cost_centers/lib/types'

import { CostCenterEmployee } from './types'

const ACTIVE = 'active'
const TERMINATED = 'terminated'

interface Filters {
  withCostCenterAssignments?: boolean[]
  legalEntityIds?: number[]
  teamIds?: number[]
  costCenterIds?: number[]
  employeeIds?: number[]
  peopleStatus?: string[]
}

const CostCenterEmployeeList = ({ costCenters }: { costCenters: CostCenterDetails[] }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  const client = useApolloClient()
  const { dev_test_de_verdad, canManageCostCenters, hasFeatureCostCenterManagement } =
    useOutletContext<{
      dev_test_de_verdad: boolean
      canManageCostCenters: boolean
      hasFeatureCostCenterManagement: boolean
    }>()

  const mapData = useUnsafeCallback((data: CostCenterEmployeeListQuery) => {
    const edges = data.employees.employeesConnection.edges
    return edges.map(({ node }) => ({ ...node }))
  }, [])

  const filterByAssignmentStatus = useUnsafeCallback((assignmentStatus?: boolean[]) => {
    if (
      assignmentStatus === undefined ||
      assignmentStatus.length === 0 ||
      (assignmentStatus.includes(true) && assignmentStatus.includes(false))
    ) {
      return {}
    }

    return { withCostCenterAssignments: assignmentStatus.includes(true) }
  }, [])

  const filterByPeopleStatus = useUnsafeCallback((status?: string[]) => {
    if (!status?.length || (status.includes(TERMINATED) && status.includes(ACTIVE))) {
      return {}
    }

    return { accessStatus: status }
  }, [])

  const source = useGraphqlDataCollectionSource({
    document: CostCenterEmployeeListDocument,
    mapData: (tableData: CostCenterEmployeeListQuery) => mapData(tableData),
    primaryActions: () => ({
      onClick: () => {
        navigate(navigation.settings.finance.costCenters.people.create.fullPath())
      },
      label: i18n.t('finance.cost_center_people_list.create.button'),
      icon: Pencil,
    }),
    sortings: {
      fullName: {
        label: i18n.t('finance.account_list.headers.name'),
        sortBy: 'fullName',
      },
    },
    defaultSortings: {
      field: 'fullName' as const,
      order: 'asc' as const,
    },
    search: {
      enabled: true,
    },
    filters: {
      legalEntityIds: {
        type: 'in' as const,
        label: i18n.t('finance.cost_centers_list.filters.legal_entity'),
        options: {
          options: company.legalEntities.map(({ id, legalName }) => ({
            value: id,
            label: legalName || '',
          })),
        },
      },
      costCenterIds: {
        type: 'in' as const,
        label: i18n.t('finance.cost_centers_list.filters.cost_center.label'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: CostCentersListDocument,
              fetchPolicy: 'cache-first',
            })

            return data.costCenters.costCentersConnection.edges.map((costCenter) => ({
              label: costCenter.node.name,
              value: costCenter.node.id,
            }))
          },
        },
      },
      employeeIds: {
        type: 'in' as const,
        label: i18n.t('finance.settings.cost_centers.people'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: AvatarNameSelectorDocument,
              variables: {
                companyId: company.id,
                includeTeams: false,
                includeWorkplaces: false,
              },
              fetchPolicy: 'cache-first',
            })

            return data.employees.employeeNames.map((employee) => ({
              label: employee.fullName,
              value: employee.id,
            }))
          },
        },
      },
      peopleStatus: {
        type: 'in' as const,
        label: i18n.t('finance.cost_center_people_list.people_status'),
        options: {
          options: [
            { label: i18n.t('employees.employees_list.status.active'), value: ACTIVE },
            { label: i18n.t('employees.employees_list.status.terminated'), value: TERMINATED },
          ],
        },
      },
      teamIds: {
        type: 'in' as const,
        label: i18n.t('filters.filter.team'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: EmployeesTeamsDocument,
              fetchPolicy: 'cache-first',
            })

            return data.teams.teams.map((team) => ({
              label: team.name,
              value: team.id,
            }))
          },
        },
      },
      withCostCenterAssignments: {
        type: 'in' as const,
        label: i18n.t('finance.cost_center_people_list.assignment_status'),
        options: {
          options: [
            { label: i18n.t('finance.cost_center_people_list.with_assignments'), value: true },
            {
              label: i18n.t('finance.cost_center_people_list.without_assignments'),
              value: false,
            },
          ],
        },
      },
    },
    presets: [
      {
        label: i18n.t('finance.cost_center_people_list.with_assignments'),
        filter: { withCostCenterAssignments: [true] },
      },
      {
        label: i18n.t('finance.cost_center_people_list.without_assignments'),
        filter: { withCostCenterAssignments: [false] },
      },
    ],
    presetsLoading: false,
    mapVariables: ({
      search,
      sortings,
      filters,
    }: {
      search?: string
      filters?: Filters
      sortings?: Array<{ field: string; order: string }>
    }) => {
      return {
        search: search,
        sortOrder: sortings?.[0],
        legalEntityIds: filters?.legalEntityIds?.length ? filters?.legalEntityIds : undefined,
        teamIds: filters?.teamIds?.length ? filters?.teamIds : undefined,
        costCenterIds: filters?.costCenterIds?.length ? filters?.costCenterIds : undefined,
        employeeIds: filters?.employeeIds?.length ? filters?.employeeIds : undefined,
        ...filterByAssignmentStatus(filters?.withCostCenterAssignments),
        ...filterByPeopleStatus(filters?.peopleStatus),
      }
    },
    mapPagination: (tableData: CostCenterEmployeeListQuery) => ({
      total: tableData.employees.employeesConnection.totalCount ?? 0,
    }),
    totalItemSummary: (totalCount: number) =>
      i18n.tp('finance.cost_center_people_list.count_employee_assignments', {
        count: totalCount,
      }),
    itemOnClick: (item: CostCenterEmployee) => () => {
      navigate(navigation.settings.finance.costCenters.people.edit.fullPath({ id: item.id }))
    },
    selectable: (item: CostCenterEmployee) => item.id,
    bulkActions: (selectedRows) => {
      const selectedLegalEntities = uniqBy(
        selectedRows.itemsStatus
          .filter((item) => item.checked)
          .map((item) => item.item.legalEntity),
        'id'
      )

      if (selectedLegalEntities.length > 1) {
        return {
          warningMessage: i18n.t('finance.cost_center_people_list.create.warning_message'),
        }
      }

      const hasAssignments = selectedRows.itemsStatus
        .filter((item) => item.checked)
        .some((item) => {
          const totalAssignments = item.item?.totalAssignments?.totalCount

          return totalAssignments && totalAssignments > 0
        })

      if (hasAssignments) {
        return {
          warningMessage: i18n.t(
            'finance.cost_center_people_list.create.warning_message_employee_assignments'
          ),
        }
      }

      return {
        primary: [
          {
            label: i18n.t('finance.cost_center_people_list.create.button'),
            icon: Pencil,
            id: 'create',
          },
        ],
      }
    },
  })

  if (costCenters.length === 0) {
    return <EmptyCostCenters />
  }

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={(_, selectedItems) => {
          const checkedItems = selectedItems.itemsStatus.filter((item) => item.checked)
          const employeeIds = checkedItems.map((item) => item.item.id)
          const legalEntityIds = [checkedItems[0]?.item.legalEntity?.id]

          navigate(navigation.settings.finance.costCenters.people.create.fullPath(), {
            state: {
              employeeIds,
              legalEntityIds,
            },
          })
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns: [
                {
                  sorting: 'fullName',
                  label: i18n.t('finance.cost_centers_list.headers.name'),
                  render: (item: CostCenterEmployee) => {
                    return {
                      type: 'person',
                      value: {
                        firstName: item.firstName ?? '',
                        lastName: item.lastName ?? '',
                        src: buildAvatarUrl(item.avatar?.url),
                        deactivated: !item.active,
                      },
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.legal_entity'),
                  render: (item: CostCenterEmployee) => {
                    return {
                      type: 'text',
                      value: item.legalEntity?.legalName,
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_center_people_list.current_assignments'),
                  render: (item: CostCenterEmployee) => {
                    if (!item.currentAssignments?.nodes.length) return { type: 'text', value: '-' }

                    return {
                      type: 'tagList',
                      value: {
                        type: 'raw',
                        max: 2,
                        tags:
                          item.currentAssignments?.nodes.map((assignment) => {
                            const text = assignment.costCenter.code
                              ? `${assignment.costCenter.code} - ${assignment.costCenter.name}`
                              : assignment.costCenter.name

                            return {
                              text: `${text} (${(assignment.percentage * 100).toFixed(2)}%)`,
                            }
                          }) || [],
                      },
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_center_people_list.current_start_date'),
                  render: (item: CostCenterEmployee) => {
                    return { type: 'text', value: item.currentAssignments?.nodes[0]?.startDate }
                  },
                },
                {
                  label: i18n.t('finance.cost_center_people_list.current_end_date'),
                  render: (item: CostCenterEmployee) => {
                    return { type: 'text', value: item.currentAssignments?.nodes[0]?.endDate }
                  },
                },
                {
                  label: i18n.t('finance.cost_center_people_list.upcoming_assignments'),
                  render: (item: CostCenterEmployee) => {
                    if (!item.upcomingAssignments?.nodes.length) return { type: 'text', value: '-' }

                    return {
                      type: 'tagList',
                      value: {
                        type: 'raw',
                        max: 2,
                        tags:
                          item.upcomingAssignments?.nodes.map((assignment) => {
                            const text = assignment.costCenter.code
                              ? `${assignment.costCenter.code} - ${assignment.costCenter.name}`
                              : assignment.costCenter.name

                            return {
                              text: `${text} (${(assignment.percentage * 100).toFixed(2)}%)`,
                            }
                          }) || [],
                      },
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_center_people_list.upcoming_assignments_start_date'),
                  render: (item: CostCenterEmployee) => {
                    return { type: 'text', value: item.upcomingAssignments?.nodes[0]?.startDate }
                  },
                },
                {
                  label: i18n.t('finance.cost_center_people_list.upcoming_assignments_end_date'),
                  render: (item: CostCenterEmployee) => {
                    return { type: 'text', value: item.upcomingAssignments?.nodes[0]?.endDate }
                  },
                },
              ],
            },
          },
        ]}
      />
      <Outlet
        context={{
          dev_test_de_verdad,
          canManageCostCenters,
          hasFeatureCostCenterManagement,
        }}
      />
    </>
  )
}

export { CostCenterEmployeeList }
